// Shared storm constants & geo helpers for the TAB Spatial Report
import * as THREE from "three";

export const GOLD = "#D4AF37";
export const GOLD_VEC = new THREE.Color(GOLD);

// Saffir-Simpson style ladder (mirrors backend INTENSITY_TABLE)
export const LEVELS = [
  { level: 0, label: "Tropical Storm", wind: 65, pressure: 1000, color: "#3B82F6" },
  { level: 1, label: "Category 1", wind: 85, pressure: 980, color: "#22D3EE" },
  { level: 2, label: "Category 2", wind: 103, pressure: 965, color: "#84CC16" },
  { level: 3, label: "Category 3", wind: 120, pressure: 945, color: "#F59E0B" },
  { level: 4, label: "Category 4", wind: 143, pressure: 920, color: "#F97316" },
  { level: 5, label: "Cat 5 Super Storm", wind: 175, pressure: 900, color: "#EF4444" },
];

// Smooth interpolation of readouts as the slider moves (0..5 continuous)
export function readoutsFor(intensity) {
  const t = Math.max(0, Math.min(5, intensity));
  const lo = LEVELS[Math.floor(t)];
  const hi = LEVELS[Math.min(5, Math.ceil(t))];
  const f = t - Math.floor(t);
  const wind = Math.round(lo.wind + (hi.wind - lo.wind) * f);
  const pressure = Math.round(lo.pressure + (hi.pressure - lo.pressure) * f);
  const idx = Math.round(t);
  return { wind, pressure, label: LEVELS[idx].label, color: LEVELS[idx].color, level: idx };
}

// Blue -> Red handle color as intensity climbs to Cat 5
export function handleColor(intensity) {
  const t = Math.max(0, Math.min(5, intensity)) / 5;
  const c = new THREE.Color("#3B82F6").lerp(new THREE.Color("#EF4444"), t);
  return `#${c.getHexString()}`;
}

// Lat/Lon -> position on a sphere of given radius (equirectangular map convention)
export function latLonToVec3(lat, lon, radius = 1) {
  const phi = (90 - lat) * (Math.PI / 180);
  const theta = (lon + 180) * (Math.PI / 180);
  return new THREE.Vector3(
    -radius * Math.sin(phi) * Math.cos(theta),
    radius * Math.cos(phi),
    radius * Math.sin(phi) * Math.sin(theta)
  );
}

// UV coordinate of a lat/lon on the earth texture (for shader storm centering)
export function latLonToUV(lat, lon) {
  return new THREE.Vector2((lon + 180) / 360, (lat + 90) / 180);
}

export const NODES = {
  hurricane_polo: {
    id: "hurricane_polo",
    name: "Hurricane Polo",
    subtitle: "Warm-core Cyclogenesis · Eastern Pacific",
    kind: "warm",
    lat: 16,
    lon: -98,
    // Eastern Pacific genesis (10N,90W) -> NW toward Acapulco landfall (16.9N,99.9W) -> inland (18N,101W)
    track: [
      { lat: 10, lon: -90 }, { lat: 12, lon: -93 }, { lat: 14, lon: -96 },
      { lat: 16, lon: -98.5 }, { lat: 16.9, lon: -99.9 }, { lat: 18, lon: -101 },
    ],
  },
  noreaster: {
    id: "noreaster",
    name: "Atlantic Nor'easter",
    subtitle: "Cold-core Baroclinic Dynamics · US East Coast",
    kind: "cold",
    lat: 39,
    lon: -72,
    // 'L-Curve': Cape Hatteras (35N,75W) -> NE past NYC & Boston -> Gulf of Maine (43N,68W)
    track: [
      { lat: 35, lon: -75 }, { lat: 37, lon: -73 }, { lat: 39, lon: -72 },
      { lat: 40.7, lon: -71.5 }, { lat: 42.4, lon: -70 }, { lat: 43, lon: -68 },
    ],
  },
};

export const CITIES = [
  { id: "nyc", name: "New York City", lat: 40.71, lon: -74.0, climate: "temperate" },
  { id: "boston", name: "Boston", lat: 42.36, lon: -71.06, climate: "temperate" },
  { id: "acapulco", name: "Acapulco", lat: 16.86, lon: -99.9, climate: "tropical" },
  { id: "new_orleans", name: "New Orleans", lat: 29.95, lon: -90.07, climate: "tropical" },
];

export function trackToPoints(track, radius) {
  return track.map((p) => latLonToVec3(p.lat, p.lon, radius));
}

export function lerpTrack(track, t) {
  if (!track || track.length === 0) return { lat: 0, lon: 0 };
  const clamped = Math.max(0, Math.min(1, t));
  const seg = clamped * (track.length - 1);
  const i = Math.floor(seg);
  const f = seg - i;
  const a = track[i];
  const b = track[Math.min(track.length - 1, i + 1)];
  return { lat: a.lat + (b.lat - a.lat) * f, lon: a.lon + (b.lon - a.lon) * f };
}

// Top-N most intense storms by peak wind (for the landing Featured Hero cycle)
export function topByWind(events, n = 5) {
  return [...(events || [])].sort((a, b) => (b.peak_wind_mph || 0) - (a.peak_wind_mph || 0)).slice(0, n);
}

export function levelForWind(w) {
  if (w >= 157) return 5; if (w >= 130) return 4; if (w >= 111) return 3;
  if (w >= 96) return 2; if (w >= 74) return 1; return 0;
}

// Breadcrumb metadata (category + wind) at a given fraction along a storm's life
export function breadcrumbAt(peakWind, frac) {
  const wind = Math.round((peakWind || 100) * (0.55 + 0.45 * frac));
  const lvl = levelForWind(wind);
  return { wind, level: lvl, label: lvl > 0 ? `C${lvl}` : "TS" };
}
